import { useCallback, useMemo, useRef, useState } from 'react';
import { useLoaderData } from 'react-router';
import { useImpression, useTracker } from '@archipro-website/tracker';
import { Calendar, Plus } from 'lucide-react';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import type { ProjectTeamMemberView } from '@modules/professional/type/project-team-member';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailScrollReveal,
    SECTION_TITLE_TYPE,
    TeamMemberOverlay,
} from '~/ui-rocco/pages/detail-shared';
import { cn } from '@rocco/utils/cn';
import { button } from '~/ui-rocco/button-system';
import { useBookCta } from '~/modules/enquiry/hooks/use-page-ctas';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import { useRailTileClickReport } from '~/ui-rocco/hooks/tile-click-event-for-section';

/** What a tile needs to open the booking gate on this person's own practice. */
interface TeamTileBooking {
    professionalUrlSegment: string;
    professionalId: number;
    professionalName: string;
    companyLogo: string;
    logoBackgroundColor: string;
    /** Only a real name — the pane must not open on a placeholder. */
    memberName?: string;
    /** Their personal Book Meeting form, asked in place of the page's. */
    bookingFormId?: number;
    /** The session that books their calendar outright; empty means a request. */
    sessionIds: number[];
}

interface TeamTile {
    id: number;
    /** The line the tile leads with: the person, or their practice when the record has no name. */
    title: string;
    /** The line under it: the person's role, as the live section and the profile's Who we are both set it. */
    subtitle: string;
    thumbnail: string;
    /**
     * Null when the credit carries no link to a profile to book against, or
     * the business hasn't opened this person to booking.
     */
    booking: TeamTileBooking | null;
    /** The discipline the project credits them under, for the overlay. */
    category: string;
    /** Whether the title above is the person's own name or their practice. */
    isPerson: boolean;
    /** The record itself, which the overlay reads the rest of the person from. */
    member: ProjectTeamMemberView['TeamMembers'][number];
}

/**
 * The names the API hands back for a credited record that has neither its own
 * name nor a linked member. They are placeholders, not names, so a tile leads
 * with the practice instead of printing them.
 */
const PLACEHOLDER_NAMES = new Set(['Unnamed Team Member', 'Unnamed Member']);

/** The practice's profile slug, from the Link the credit carries. */
const urlSegmentFromLink = (link: string) =>
    link.split('/').filter(Boolean).pop() ?? '';

/**
 * The payload a member's team events carry: their practice, and the category
 * and name they are credited under.
 */
const teamMemberEventData = (tile: TeamTile) => ({
    ProfessionalID: tile.member.ProfessionalID,
    ExtraData: JSON.stringify({
        category: tile.category,
        member: tile.member.Name,
    }),
});

/**
 * One member, as the profile's Who we are sets a member: portrait, name,
 * practice, with Book a meeting resting on the portrait.
 *
 * The add control is its mirror on the right: square, so the pair reads as one
 * row of controls on the photograph rather than two different button families.
 */
const TeamSlide = ({
    tile,
    onBookMeeting,
    onOpen,
}: {
    tile: TeamTile;
    onBookMeeting?: (tile: TeamTile) => void;
    onOpen: (tile: TeamTile) => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const tracker = useTracker();
    useImpression({
        ref,
        onImpression: async (url) => {
            await tracker.log('ProjectTeamImpression', {
                url,
                data: teamMemberEventData(tile),
            });
        },
    });

    return (
        <div ref={ref} className="flex flex-col">
            <div className="relative aspect-[437/524] w-full overflow-hidden rounded-tile bg-white/5">
                {tile.thumbnail ? (
                    <img
                        src={tile.thumbnail}
                        alt={tile.title}
                        loading="lazy"
                        className="absolute inset-0 size-full object-cover"
                    />
                ) : null}
                {onBookMeeting ? (
                    <button
                        type="button"
                        onClick={() => onBookMeeting(tile)}
                        // Named here because the label is hidden on a phone.
                        aria-label="Book a meeting"
                        className={button(
                            'lifted',
                            ['m', 'l'],
                            'absolute bottom-4 left-4',
                            { icon: 'max-md' },
                        )}
                    >
                        <Calendar
                            aria-hidden="true"
                            className="size-[1.125rem] shrink-0"
                        />
                        <span className="max-md:hidden">Book a meeting</span>
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => onOpen(tile)}
                    aria-label={`About ${tile.title}`}
                    className={button(
                        'lifted',
                        ['m', 'l'],
                        'absolute right-4 bottom-4',
                        { icon: true },
                    )}
                >
                    <Plus
                        aria-hidden="true"
                        className="size-[1.125rem] shrink-0"
                    />
                </button>
            </div>
            <p className="m-0 font-sans leading-snug font-medium text-white max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[0.875rem]">
                {tile.title}
            </p>
            {tile.subtitle ? (
                <p className="m-0 font-sans leading-snug text-white/40 max-md:text-sm md:text-[0.875rem]">
                    {tile.subtitle}
                </p>
            ) : null}
        </div>
    );
};

/**
 * Project team — the people the project credits, in the profile's Who we are
 * layout: the title, then a full-bleed four-column carousel of portraits.
 *
 * The title is set in the display serif the page's other section heads use,
 * rather than the profile's sans: this page speaks in the editorial voice.
 *
 * The loader groups the members by their credited category and a person can be
 * credited in more than one, so the list is flattened and deduplicated. Hides
 * itself when the project credits nobody.
 */
export const ProjectTeamSection = () => {
    const { project, projectTeamMembers } = useLoaderData<typeof loader>();
    const { ctas } = usePageCtas();
    const book = useBookCta({ source: 'ProjectTeamSection' });
    const [openTile, setOpenTile] = useState<TeamTile | null>(null);
    const tracker = useTracker();
    const reportPracticeClick = useRailTileClickReport(
        'ProjectUsedProfessionals',
        'ProfessionalTile',
    );
    const openMember = useCallback(
        (tile: TeamTile) => {
            void tracker.log('ProjectTeamClick', {
                url: new URL(window.location.href),
                data: teamMemberEventData(tile),
            });
            setOpenTile(tile);
        },
        [tracker],
    );

    // Built once per payload: the tiles feed a carousel and an overlay that
    // both hold one of them in state, so rebuilding the array on every render
    // hands the slides a new object each time the overlay opens or closes.
    const tiles = useMemo(() => {
        const seen = new Set<number>();
        const built: TeamTile[] = [];
        for (const group of projectTeamMembers ?? []) {
            for (const member of group.TeamMembers ?? []) {
                if (seen.has(member.ID)) continue;
                seen.add(member.ID);
                const named =
                    member.Name && !PLACEHOLDER_NAMES.has(member.Name);
                const professionalUrlSegment = urlSegmentFromLink(
                    member.Link ?? '',
                );
                built.push({
                    id: member.ID,
                    title: named ? member.Name : member.ProfessionalName,
                    subtitle: member.JobTitle,
                    thumbnail: member.Thumbnail,
                    category: group.Category,
                    isPerson: Boolean(named),
                    member,
                    booking:
                        professionalUrlSegment && member.BookingEnabled
                            ? {
                                  professionalUrlSegment,
                                  professionalId: member.ProfessionalID,
                                  professionalName: member.ProfessionalName,
                                  companyLogo: member.CompanyLogo ?? '',
                                  logoBackgroundColor:
                                      member.LogoBackgroundColor ?? '',
                                  memberName: named ? member.Name : undefined,
                                  bookingFormId:
                                      member.BookingFormID ?? undefined,
                                  sessionIds: member.BookableCalendarSessionID
                                      ? [member.BookableCalendarSessionID]
                                      : [],
                              }
                            : null,
                });
            }
        }
        return built;
    }, [projectTeamMembers]);

    if (tiles.length === 0) return null;

    // Each tile books its own person at their own practice, which need not be
    // the one that published the project, so the gate opens on the member's
    // practice, personal form and session rather than the page's. The tiles
    // only offer it while the page's Book a meeting button opens the gate.
    const onBookMeeting =
        book.visible && ctas.book.viaGate
            ? (tile: TeamTile) => {
                  if (!tile.booking) return;
                  book.openFor({
                      professional: {
                          urlSegment: tile.booking.professionalUrlSegment,
                          id: tile.booking.professionalId,
                          name: tile.booking.professionalName,
                          logo: tile.booking.companyLogo,
                          logoBackgroundColor: tile.booking.logoBackgroundColor,
                      },
                      sessionIds: tile.booking.sessionIds,
                      bookingFormId: tile.booking.bookingFormId,
                      preferredTeamMemberId: tile.id,
                      preferredTeamMemberName: tile.booking.memberName,
                      paneImage: tile.thumbnail || undefined,
                  });
              }
            : undefined;

    return (
        <section className="overflow-hidden">
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-5 md:px-[3rem] md:pt-[1.875rem]">
                <h2 className={cn('m-0 text-white', SECTION_TITLE_TYPE)}>
                    Project team
                </h2>
            </div>
            <DetailScrollReveal
                delay={120}
                className="w-full max-md:pt-8 md:pt-[3rem]"
            >
                <Carousel
                    ariaLabel="Project team"
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Gutter padding on the viewport keeps the first and last
                        snaps on the page gutter while the slides run full-bleed
                        to the screen's edge, as the profile's rail does. */}
                    <CarouselContent
                        className="max-md:gap-3 md:-mx-[0.375rem]"
                        viewportClassName="max-md:px-4 md:px-[3rem] qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                    >
                        {tiles.map((tile) => (
                            <CarouselItem
                                key={tile.id}
                                className="max-md:basis-[46%] md:basis-1/4 md:px-[0.375rem]"
                            >
                                <TeamSlide
                                    tile={tile}
                                    onBookMeeting={
                                        tile.booking ? onBookMeeting : undefined
                                    }
                                    onOpen={openMember}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </DetailScrollReveal>
            {openTile ? (
                <TeamMemberOverlay
                    displayName={openTile.title}
                    isPerson={openTile.isPerson}
                    jobTitle={openTile.member.JobTitle}
                    summary={openTile.member.Summary}
                    thumbnail={openTile.member.Thumbnail}
                    licenseNumber={openTile.member.LicenseNumber}
                    practice={{
                        id: openTile.member.ProfessionalID,
                        name: openTile.member.ProfessionalName,
                        link: openTile.member.Link,
                        companyLogo: openTile.member.CompanyLogo,
                        logoBackgroundColor:
                            openTile.member.LogoBackgroundColor,
                        customEnquiryMessage:
                            openTile.member.CustomEnquiryMessage,
                        enquiryMessageOverrideEnabled:
                            openTile.member.EnquiryMessageOverrideEnabled,
                        enquiryMessagePlaceholder:
                            openTile.member.EnquiryMessagePlaceholder,
                    }}
                    subject={{
                        itemType: 'project',
                        siteTreeID: project.ID,
                        itemTitle: project.Title,
                        relatedItem: {
                            id: project.ID,
                            type: 'project',
                            title: project.Title,
                            category: project.categoryPath,
                        },
                    }}
                    onBookMeeting={
                        onBookMeeting && openTile.booking
                            ? () => onBookMeeting(openTile)
                            : undefined
                    }
                    source="ProjectTeamSection"
                    onPracticeClick={() =>
                        reportPracticeClick({
                            id: openTile.member.ProfessionalID,
                            title: openTile.member.ProfessionalName,
                            professionalId: openTile.member.ProfessionalID,
                        })
                    }
                    onClose={() => setOpenTile(null)}
                />
            ) : null}
        </section>
    );
};
